import { KindeContextProps } from '@kinde-oss/kinde-auth-react';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import axios from 'axios';

import { Flex } from '@/components/core';
import { CRADL_ORGANIZATION, EXTERNAL_ORG_ID_CLAIM, ONBOARDING_COMPLETED_SESSION_KEY } from '@/constants';
import { withRequiredClaims } from '@/features/auth';
import { checkOrganizationTrialStatus } from '@/hooks';
import { createClientFromAuth, withRetry } from '@/utils';

const AuthContainer = () => (
  <Flex col className="h-screen">
    <Outlet />
  </Flex>
);

const updateStashedOrganizationDetails = async (auth: KindeContextProps) => {
  const organizationProps = localStorage.getItem(ONBOARDING_COMPLETED_SESSION_KEY);
  localStorage.removeItem(ONBOARDING_COMPLETED_SESSION_KEY);

  if (organizationProps != null) {
    try {
      const client = await createClientFromAuth(auth);

      const errorCond = (error: Error) =>
        axios.isAxiosError(error) && error?.response?.status != null && [403, 401].includes(error.response.status);

      await withRetry({
        fn: () => client?.updateOrganization('me', JSON.parse(organizationProps)),
        errorCondition: errorCond,
        delay: 2000,
      });
    } catch (error) {
      console.error(error);
    }
  }
};

const checkTrailExpired = async (auth: KindeContextProps) => {
  const client = await createClientFromAuth(auth);

  const organization = await withRetry({ fn: () => client.getOrganization('me'), delay: 1500 });
  const { hasExpired, hasUpgraded } = checkOrganizationTrialStatus(organization);

  const routesAllowedPastExpiration = ['/settings', '/upgrade', '/organizations'];
  const isAllowedPastExpiration = routesAllowedPastExpiration.some((r) => location.pathname.startsWith(r));

  if (hasExpired && !hasUpgraded && !isAllowedPastExpiration) {
    throw redirect({
      to: '/upgrade',
    });
  }
};

export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context, location }) => {
    const { auth } = context;

    if (!auth.isAuthenticated) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    } else {
      const claims = await withRequiredClaims(auth);

      if (claims) {
        if (claims[EXTERNAL_ORG_ID_CLAIM] !== CRADL_ORGANIZATION) {
          await updateStashedOrganizationDetails(auth);
          await checkTrailExpired(auth);
        }
      }
    }
  },
  loader: async ({ context: { auth } }) => {
    const claim = await auth.getClaim(EXTERNAL_ORG_ID_CLAIM);
    return { organizationId: claim?.value as string | undefined | null };
  },
  component: AuthContainer,
});
